import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import moment from 'moment';
const ChangeLogsAccordions = ({ changeLogs, dateRange }) => {
    const [expandedMain, setExpandedMain] = React.useState(false);
    const [expandedMonth, setExpandedMonth] = React.useState(false);
    const handleMainChange = (panel) => (event, isExpanded) => {
        setExpandedMain(isExpanded ? panel : false);
    };
    const handleMonthChange = (panel) => (event, isExpanded) => {
        setExpandedMonth(isExpanded ? panel : false);
    };
    // Filter logs for the selected date range
    const rangeFilteredLogs = useMemo(() => {
        if (!dateRange || !dateRange.start || !dateRange.end)
            return [];
        const start = moment(dateRange.start.toString(), "YYYY-MM-DD").startOf('day');
        const end = moment(dateRange.end.toString(), "YYYY-MM-DD").endOf('day');
        return changeLogs.filter(log => {
            const logDate = moment(log.date);
            return logDate.isBetween(start, end, null, '[]');
        });
    }, [changeLogs, dateRange]);
    // Group all logs by month for the complete change log
    const groupedLogs = useMemo(() => {
        const groups = {};
        changeLogs.forEach(log => {
            const monthYear = moment(log.date).format('MMMM YYYY');
            if (!groups[monthYear]) {
                groups[monthYear] = [];
            }
            groups[monthYear].push(log);
        });
        // Sort months descending (assuming keys can just be sorted or they are already sorted since logs are sorted descending from backend)
        // Since backend already sorts by date descending, the insertion order will be roughly correct, but let's ensure correct key sorting.
        return Object.entries(groups).sort((a, b) => {
            return moment(b[0], 'MMMM YYYY').valueOf() - moment(a[0], 'MMMM YYYY').valueOf();
        });
    }, [changeLogs]);
    if (!changeLogs || changeLogs.length === 0) {
        return null;
    }
    const renderGroupedLogItems = (logsToRender) => {
        const groups = {};
        logsToRender.forEach(log => {
            const dateStr = moment(log.date).format('MM/DD/YYYY');
            if (!groups[dateStr])
                groups[dateStr] = [];
            groups[dateStr].push(log);
        });
        const sortedGroups = Object.entries(groups).sort((a, b) => moment(b[0], 'MM/DD/YYYY').valueOf() - moment(a[0], 'MM/DD/YYYY').valueOf());
        return sortedGroups.map(([dateStr, items]) => (_jsxs(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                py: 2,
                px: 3,
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': { borderBottom: 'none' }
            }, children: [_jsx(Typography, { sx: { fontWeight: 600, color: '#111827', fontSize: '15px', mb: 1 }, children: dateStr }), _jsx(Box, { component: "ol", sx: { m: 0, pl: 2, color: '#4B5563', fontSize: '14px' }, children: items.map((log) => (_jsx("li", { style: { marginBottom: '6px' }, children: log.description }, log._id))) })] }, dateStr)));
    };
    const accordionStyle = {
        boxShadow: 'none',
        border: '1px solid #D0D5DD',
        bgcolor: '#fff',
        color: '#111827',
        borderRadius: '8px !important',
        mb: 1,
        '&:before': {
            display: 'none',
        },
    };
    const accordionSummaryStyle = {
        minHeight: '48px',
        bgcolor: '#f4f4f5',
        '& .MuiAccordionSummary-content': {
            my: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        }
    };
    return (_jsxs(Box, { sx: { width: '100%' }, children: [rangeFilteredLogs.length > 0 && (_jsxs(Accordion, { expanded: expandedMain === 'panel1', onChange: handleMainChange('panel1'), sx: accordionStyle, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: '#6B7280' } }), sx: accordionSummaryStyle, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: '15px' }, children: "Developer Changes Within Selected Date Range" }), dateRange?.start && dateRange?.end && (_jsxs(Typography, { sx: { fontSize: '13px', color: '#6B7280', mr: 2 }, children: [moment(dateRange.start.toString(), "YYYY-MM-DD").format('MM/DD/YYYY'), " - ", moment(dateRange.end.toString(), "YYYY-MM-DD").format('MM/DD/YYYY')] }))] }), _jsx(AccordionDetails, { sx: { bgcolor: '#fff', color: '#000', p: 0, border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 8px 8px' }, children: renderGroupedLogItems(rangeFilteredLogs) })] })), _jsxs(Accordion, { expanded: expandedMain === 'panel2', onChange: handleMainChange('panel2'), sx: {
                    ...accordionStyle,
                    mb: 0
                }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: '#6B7280' } }), sx: accordionSummaryStyle, children: _jsx(Typography, { sx: { fontWeight: 600, fontSize: '15px' }, children: "Complete Change Log" }) }), _jsx(AccordionDetails, { sx: { bgcolor: '#F9FAFB', color: '#000', p: 2, border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 8px 8px' }, children: groupedLogs.length > 0 ? (groupedLogs.map(([monthYear, logs], index) => {
                            const isExpanded = expandedMonth === monthYear;
                            return (_jsxs(Accordion, { expanded: isExpanded, onChange: handleMonthChange(monthYear), sx: {
                                    boxShadow: 'none',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px !important',
                                    mb: 1,
                                    '&:before': { display: 'none' },
                                }, children: [_jsxs(AccordionSummary, { expandIcon: isExpanded ? _jsx(RemoveIcon, { sx: { fontSize: 16 } }) : _jsx(AddIcon, { sx: { fontSize: 16 } }), sx: {
                                            minHeight: '50px !important',
                                            bgcolor: '#f4f4f5',
                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                                position: 'absolute',
                                                left: 16
                                            },
                                            '& .MuiAccordionSummary-content': {
                                                ml: 4,
                                                my: '0 !important',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }
                                        }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: '14px', color: '#111827' }, children: monthYear }), _jsxs(Box, { sx: {
                                                    bgcolor: '#F3F4F6',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '16px',
                                                    border: '1px solid #E5E7EB',
                                                    fontSize: '12px',
                                                    color: '#6B7280'
                                                }, children: [logs.length, " changes"] })] }), _jsx(AccordionDetails, { sx: { p: 0, borderTop: '1px solid #F3F4F6' }, children: renderGroupedLogItems(logs) })] }, monthYear));
                        })) : (_jsx(Box, { sx: { p: 3, textAlign: 'center', color: '#6B7280' }, children: "No change logs available." })) })] })] }));
};
export default ChangeLogsAccordions;
